import React, { useEffect, useReducer } from "react";
import "../../app/App.scss";
import { IntlProvider } from "react-intl";
import ENGLISH from "../../i18n/languages/en-US.json";
import { pig } from "../../static/js/pig.js";
import Header from "../laborMarketHtml/Header";
import { sendRequest } from "../../common/http";
import InstitutionRegionContext from "../laborMarketHtml/InstitutionRegionContext";
import TrendsExport from "../laborMarketHtml/TrendsExport";
import Loader from "../common/elements/Loader";
import translate from "@/i18n/translate";
import {
  LaborForceBreakdown,
  defaultLaborForceBreakdown,
  defaultRegionalContext,
  RegionalContext,
  defaultCoordinates
} from "./Interface";
import UnauthorizedComponent from "@/features/insights/UnauthorizedComponent";

interface Institution {
  name: string;
}
interface LoadedComponents {
  institution: boolean;
  institutionRegionContext: boolean;
  trends: boolean;
}
interface LaborMarketExportViewState {
  locale: string;
  messages: any;
  institution: Institution;
  loadedComponents: LoadedComponents;
  regionalContext: RegionalContext | null;
  auth: Boolean;
  institutionApiError: boolean;
  regionalContextApiError: boolean;
}

interface RegionalContextResultset {
  regionalContext: RegionalContext;
  showLightcastWidget: boolean;
}

interface LaborMarketExportViewAction {
  type: string;
  institution: Institution;
  locale: string | null;
  lfb: LaborForceBreakdown;
  loadedComponents: LoadedComponents;
  regionalContextResultset: RegionalContextResultset;
  component: string;
}

const defaultLoadedComponents = {
  institution: false,
  institutionRegionContext: false,
  trends: false
};

const defaultInstitution = {
  name: ""
};

const defaultRegionalContextResultset = {
  regionalContext: defaultRegionalContext,
  showLightcastWidget: false
};

const defaultLaborMarketExportViewAction = {
  type: "",
  institution: defaultInstitution,
  locale: "",
  lfb: defaultLaborForceBreakdown,
  loadedComponents: defaultLoadedComponents,
  regionalContextResultset: defaultRegionalContextResultset,
  component: ""
};

const reducer = (
  state: LaborMarketExportViewState,
  action: LaborMarketExportViewAction
) => {
  switch (action.type) {
    case "setInstitution":
      return setInstitution(state, action.institution);
    case "setLocale":
      return setLocale(state, action.locale);
    case "setRegionalContext":
      return {
        ...state,
        regionalContext: action.regionalContextResultset.regionalContext,
        auth: true
      };
    case "disableAuth":
      return {
        ...state,
        auth: false
      };

    case "setLaborForceBreakdown":
      return {
        ...state,
        laborForceBreakdown: action.lfb
      };
    case "setLoadedComponent":
      const component = action.component;
      let loadedComponents = state.loadedComponents;
      loadedComponents[component] = true;
      return { ...state, loadedTabs: loadedComponents };

    case "setInstitutionApiError":
      return { ...state, institutionApiError: true };

    case "setRegionalContextApiError":
      return { ...state, regionalContextApiError: true };

    default:
      return state;
  }
};

const setLocale = (
  state: LaborMarketExportViewState,
  locale: string | null
) => {
  switch (locale) {
    case "pig": {
      return { ...state, locale: "en-US", messages: pig(ENGLISH) };
    }
    default: {
      return { ...state, locale: "en-US", messages: ENGLISH };
    }
  }
};

const setInstitution = (state: any, institution: Institution) => {
  return {
    ...state,
    institution: institution,
    regionalContext: null,
    auth: true
  };
};
type Components = {
  institution: boolean;
  institutionRegionContext: boolean;
  trends: boolean;
};

const allComponentsLoaded = (loadedComponents: Components) => {
  return (
    loadedComponents.institution &&
    loadedComponents.institutionRegionContext &&
    loadedComponents.trends
  );
};
export interface LaborMarketExportProps {}

export default function LaborMarketExportView(): JSX.Element {
  const onComponentLoad = (component: string) => {
    dispatch({
      ...defaultLaborMarketExportViewAction,
      type: "setLoadedComponent",
      component: component
    });
  };

  useEffect(() => {
    const applyResult = (
      result: Institution | { showLightcastWidget: boolean }
    ) => {
      if ("showLightcastWidget" in result) {
        !result.showLightcastWidget &&
          dispatch({
            ...defaultLaborMarketExportViewAction,
            type: "disableAuth"
          });
      } else {
        dispatch({
          ...defaultLaborMarketExportViewAction,
          type: "setInstitution",
          institution: result
        });
      }

      onComponentLoad("institution");
    };

    const onError = () => {
      dispatch({
        ...defaultLaborMarketExportViewAction,
        type: "setInstitutionApiError"
      });
      onComponentLoad("institution");
    };

    sendRequest(
        {
          uri: `/lightcast/institution`,
          camelize: true
        },
        applyResult,
        onError
      );
  }, []);

  useEffect(() => {
    const applyResult = (result: RegionalContextResultset) => {
      if ("showLightcastWidget" in result && !result.showLightcastWidget) {
        dispatch({
          ...defaultLaborMarketExportViewAction,
          type: "disableAuth"
        });
      } else {
        dispatch({
          ...defaultLaborMarketExportViewAction,
          type: "setRegionalContext",
          regionalContextResultset: result
        });
      }
    };

    const onError = () => {
      dispatch({
        ...defaultLaborMarketExportViewAction,
        type: "setRegionalContextApiError"
      });
    };

    sendRequest(
        { uri: "/lightcast/regional_context", camelize: true },
        applyResult,
        onError
      );
  }, []);

  const initialState = {
    locale: "en-US",
    messages: null,
    institution: {},
    loadedComponents: {
      institution: false,
      institutionRegionContext: false,
      trends: false
    },
    regionalContext: {
      coordinates: defaultCoordinates,
      countyCodes: [],
      mapId: "",
      mapTilerApiKey: "",
      noOfCounties: 0,
      radius: 0,
      timeFrame: "",
      zip: 0
    },
    laborForceBreakdown: null,
    auth: true,
    regionalContextApiError: false,
    institutionApiError: false
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const queryString = window.location.search;
    const param = new URLSearchParams(queryString);
    const locale = param.get("locale");

    dispatch({
      ...defaultLaborMarketExportViewAction,
      type: "setLocale",
      locale: locale
    });
  }, []);

  const setLaborForceBreakdown = (lfb: LaborForceBreakdown) => {
    dispatch({
      ...defaultLaborMarketExportViewAction,
      type: "setLaborForceBreakdown",
      lfb: lfb
    });
  };

  return (
    <>
      {state.auth ? (
        <div className="pagewrapper participation-container">
          <div className="insights-hub-body flex-row">
            <div className="nav-main">
              <div className="contentwrapper">
                <main id="maincontent" className="main-container">
                  {state.messages && (
                    <IntlProvider
                      locale={state.locale}
                      messages={state.messages}
                    >
                      {!allComponentsLoaded(state.loadedComponents) && (
                        <Loader />
                      )}
                      <>
                        <Header title={state.institution.name} />
                        <div className="wm-grid lightcast-body-html">
                          <div className="institution-region-context">
                            {state.auth ? (
                              <InstitutionRegionContext
                                regionalContext={state.regionalContext}
                                onComponentLoad={onComponentLoad}
                              />
                            ) : (
                              ""
                            )}

                            <div className="lightcast-source">
                              {translate("app.lightcast.Source")}
                            </div>
                            <div className="widget-border"></div>
                            <div className="grid-container wm-grid-col-12 wm-grid-col-lg-12 wm-grid-col-md-10 wm-grid-col-sm-8 trends-container">
                              {state.auth ? (
                                <TrendsExport
                                  setLaborForceBreakdown={
                                    setLaborForceBreakdown
                                  }
                                  laborForceBreakdown={
                                    state.laborForceBreakdown
                                  }
                                  onComponentLoad={onComponentLoad}
                                />
                              ) : (
                                ""
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    </IntlProvider>
                  )}
                </main>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <UnauthorizedComponent />
      )}
    </>
  );
}
